// import esriId from "@arcgis/core/identity/IdentityManager";
import {
  createContext,
  useContext,
  useReducer,
  useMemo,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import api from "api";
import { useLoading, handleLoading } from "context/LoadingContext";
import handleRangedItems from "utils/handleRangedItems";
import createFilterQuery from "utils/createFilterQuery";
import addGeometryAndNamedValuesState from "utils/addGeometryAndNamedValuesState";
import addDomainNamedValuesToAttributes from "utils/addDomainNamedValuesToAttributes";
import handleCreateObjectWithInStatment from "utils/handleCreateObjectWithInStatment";
import { useEsriUrls } from "./esriUrlsContext";
import { useAuth } from "./auth";
import getEsriData from "./contextFunctions/getEsriTableDataData";
// Lookups context
const storageContext = createContext();
// Setting custom name for the context which is visible on react dev tools
storageContext.displayName = "Storage Context";

// reducer
function reducer(state, action) {
  switch (action.type) {
    case "INIT": {
      return { ...state, ...action.payload };
    }
    case "SET_VIEW": {
      return { ...state, view: action.payload };
    }
    case "SET_DATA_WITH_FILTER": {
      return { ...state, ...action.payload };
    }
    case "HANDLE_UPDATE": {
      return { ...state, ...action.payload };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// context provider
function StorageContextProvider({ children }) {
  const initialState = {
    sections: [],
    prioritySections: [],
  };
  const [controller, dispatch] = useReducer(reducer, initialState);
  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  const [, loadingDispatch] = useLoading();
  const [esriUrls] = useEsriUrls();
  const [auth] = useAuth();

  useEffect(() => {
    if (esriUrls.Violations) {
      (async function () {
        if (auth.userDetails) {
          try {
            handleLoading(loadingDispatch, true);
            const sectionsRes = await api.post("sections/search", {
              areaId: auth.userDetails.areaId,
            });

            const {
              res: prioritySections,
              // serverFeatureLayer: prioritySectionsServerFeatureLayer,
            } = await getEsriData({
              token: auth.token,
              table: esriUrls.PrioritySections,
            });

            const prioritySectionsIds = prioritySections.map(
              (item) => item.SECTION_ID
            );

            const sections = sectionsRes.data.data.map((item) => ({
              ...item,
              isPriority: prioritySectionsIds.includes(item.sectionId),
            }));
            dispatch({
              type: "INIT",
              payload: {
                sections,
                prioritySections,
              },
            });
          } catch (error) {
            console.log(error);
          }
          handleLoading(loadingDispatch, false);
          /**
           * Initialize application
           */
        }
      })();
    }
  }, [esriUrls, auth.userDetails]);

  return (
    <storageContext.Provider value={value}>{children}</storageContext.Provider>
  );
}

StorageContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// custom hook for using context
function useStorage() {
  const context = useContext(storageContext);

  if (!context) {
    throw new Error(
      "useStorage should be used inside the StorageContextProvider."
    );
  }

  return context;
}

// Context module functions

async function setView(dispatch, view) {
  dispatch({
    type: "SET_VIEW",
    payload: view,
  });
}
/**
 *
 * @param {object} dispatch
 * @param {object} data data to be filter based on it
 * @param {*} view
 * @param {*} fields
 * @param {*} mapLayer
 * @param {*} domains
 * @param {string} objName object name to be set with new filtered data
 */
async function setDataBasedOnFilter({
  dispatch,
  data,
  view,
  fields,
  featureLayer,
  domains,
  objName,
}) {
  const dataWithRangeHandled = handleRangedItems(data);
  const { objectWithoutIns, objectWithIn } =
    handleCreateObjectWithInStatment(dataWithRangeHandled);
  const query = createFilterQuery(objectWithoutIns, fields, objectWithIn);
  const result = await featureLayer.queryFeatures({
    where: query || `1=1`,
    outFields: "*",
    returnGeometry: true,
    outSpatialReference: view.spatialReference,
  });

  // extract attributes from features and add them to the new variable
  const res = addGeometryAndNamedValuesState(result.features, domains);
  dispatch({ type: "INIT", payload: { [objName]: res } });
}

async function updateAfterDelete({
  dispatch,
  objIdToDelete,
  data,
  targetName,
  // view,
  // fields,
  // buildingsLayer,
  // domains
}) {
  const updatedData = data.filter((item) => item.OBJECTID !== objIdToDelete);

  dispatch({
    type: "INIT",
    payload: { [targetName]: updatedData },
  });
}

async function updateAfterEdit({
  dispatch,
  newObj,
  data,
  targetName,
  domains,
}) {
  const tempData = JSON.parse(JSON.stringify(data));
  const tempNewObj = JSON.parse(JSON.stringify(newObj));
  // add named domains to the new object
  let newObjWithNamedDomains = {};
  if (domains) {
    newObjWithNamedDomains = addDomainNamedValuesToAttributes(domains, newObj);
  }

  const foundIdx = tempData.findIndex(
    (item) => item.OBJECTID === tempNewObj.OBJECTID
  );

  if (foundIdx !== -1)
    tempData[foundIdx] = {
      ...tempData[foundIdx],
      ...tempNewObj,
      ...newObjWithNamedDomains,
    };
  // don't use this because it will not add Object Id
  else {
    tempData.push({
      ...tempNewObj,
      ...newObjWithNamedDomains,
      CREATED_DATE: new Date().getTime(),
      CREATED_DATE_ISO: new Date().toISOString(),
      date: new Date().toISOString(),
    });
  }

  dispatch({
    type: "INIT",
    payload: { [targetName]: tempData },
  });
}

function addNewItem({ dispatch, data, newObj, targetName }) {
  dispatch({
    type: "INIT",
    payload: { [targetName]: [newObj, ...data] },
  });
}
function replaceOldState({ dispatch, data, targetName }) {
  dispatch({
    type: "INIT",
    payload: { [targetName]: [...data] },
  });
}

async function dispatchContracts(dispatch, data) {
  dispatch({
    type: "SET_CONTRACTS",
    payload: data,
  });
}

export {
  StorageContextProvider,
  useStorage,
  setView,
  setDataBasedOnFilter,
  updateAfterDelete,
  updateAfterEdit,
  addNewItem,
  replaceOldState,
  dispatchContracts,
};
